import React from "react";
import { useThemeMode } from "@/lib/themes/theme-mode";

interface TabShapeSVGProps {
  width: number;
  height: number;
  isActive: boolean;
  isHovered: boolean;
  isStacked: boolean;
  isLeftStacked: boolean;
  stackIndex: number;
  isPdf?: boolean;
}

export const TabShapeSVG: React.FC<TabShapeSVGProps> = ({
  width,
  height,
  isActive,
  isHovered,
  isStacked,
  isLeftStacked,
  stackIndex,
  isPdf = false,
}) => {
  const { theme } = useThemeMode();
  const isLight = theme.name === "light";

  const w = width;
  const h = height;

  const leftInset = 2;

  // Smooth Chrome Tab Shape with C1 continuous cubic Bezier curves across all 4 corners
  let d = `M 0 ${h} `;
  d += `C 5 ${h} 10.5 45 12 38 `;
  d += `L 18 10 `;
  d += `C 19.5 3 24 0 30 0 `;
  d += `L ${w - 30} 0 `;
  d += `C ${w - 24} 0 ${w - 19.5} 3 ${w - 18} 10 `;
  d += `L ${w - 12} 38 `;
  d += `C ${w - 10.5} 45 ${w - 5} ${h} ${w} ${h} `;

  const strokeD = d; // Open path: stroke only covers top edge and side slash curves

  if (!isActive) {
    d += ` L 0 ${h} Z`;
  }

  const baseVal = isActive ? 255 : (isHovered ? 245 : 237);
  let fillR = baseVal;
  let fillG = baseVal;
  let fillB = baseVal;

  // Depth darkening for stacked cards at boundaries
  if (isStacked && !isActive) {
    const darkenFactor = Math.max(0.7, 1.0 - stackIndex * 0.1);
    fillR = Math.round(fillR * darkenFactor);
    fillG = Math.round(fillG * darkenFactor);
    fillB = Math.round(fillB * darkenFactor);
  }

  const fillStyle = isLight ? `rgb(${fillR}, ${fillG}, ${fillB})` : theme.bgSurface;
  const strokeColor = theme.borderDefault;

  const tabShadowId = `shadow-tab-${Math.round(width)}`;
  const innerGradId = `shadow-inner-bottom-${Math.round(width)}`;

  return (
    <svg
      width={width + 4}
      height={height + 8}
      className="absolute top-0 left-0 overflow-visible pointer-events-none"
    >
      <defs>
        {/* Dual-stage drop shadow for full contour coverage across top corners, top edge, and sides without bottom shadow bleeding */}
        <filter id={tabShadowId} x="-30%" y="-30%" width="160%" height="160%">
          {/* Top Edge & Top Corner Radius Shadow */}
          <feDropShadow dx="0" dy="-1" stdDeviation="2" floodColor="#000000" floodOpacity="0.20" />
          {/* Side Slash Contour Shadow */}
          <feDropShadow dx="1.5" dy="-0.5" stdDeviation="2" floodColor="#000000" floodOpacity="0.18" />
        </filter>

        {/* Tight, subtle Bottom Inner Shadow Linear Gradient */}
        <linearGradient id={innerGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="88%" stopColor="#000000" stopOpacity="0" />
          <stop offset="94%" stopColor="#000000" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.14" />
        </linearGradient>
      </defs>

      {/* Main Path Fill (Active and Inactive tabs use dual-stage full-contour drop shadow) */}
      <path
        d={d}
        fill={fillStyle}
        filter={`url(#${tabShadowId})`}
      />

      {/* Inactive Tab Tight Bottom Inner Shadow Overlay */}
      {!isActive && (
        <path
          d={d}
          fill={`url(#${innerGradId})`}
        />
      )}

      {/* Active Tab Seam Bridge */}
      {isActive && (
          <rect
              x={12}
              y={h - 0.5}
              width={w - 20}
              height={6}
              fill={isLight ? "#FFFFFF" : theme.bgSurface}
          />
      )}

      {/* 0.5px Stroke Border: strokeD only outlines top and side curves, never bottom baseline */}
      <path
        d={strokeD}
        fill="none"
        stroke={strokeColor}
        strokeWidth={0.5}
      />
    </svg>
  );
};
